const OutputEntireFilePrompt =
  'It is very important that you OUTPUT THE ENTIRE FILE with the changes applied and nothing else. Only apply the provided changes and do not make any other changes.'

/**
 * Get the prompt for applying changes to a file.
 *
 * @param originalFileContents - The original file contents.
 * @param change - The change to apply to the file.
 * @returns The prompt for applying changes to a file.
 */
export function getApplyChangesPrompt(
  originalFileContents: string,
  change: {
    language: string
    location: string
    type: 'edit' | 'add' | 'remove' | 'entire_file'
    code: string
  },
): { role: 'user'; content: string }[] {
  const actionString = (() => {
    switch (change.type) {
      case 'entire_file':
        throw new Error('Should not be using this function for entire file changes.')
      case 'edit':
        return 'You are given a code file and a block of code. Edit the file to include the block of code.'
      case 'add':
        return 'You are given a code file and a block of code. Add the block of code to the file.'
      case 'remove':
        return 'You are given a code file and a block of code. Remove the block of code from the file.'
    }
  })()

  const originalFileCodeBlock = `\`\`\`${change.language}\n${originalFileContents}\n\`\`\``
  return [
    {
      role: 'user',
      content: `${actionString} ${OutputEntireFilePrompt}`,
    },
    {
      role: 'user',
      content: originalFileCodeBlock,
    },
    {
      role: 'user',
      content: `Change location: ${change.location}`,
    },
    {
      role: 'user',
      content: `\`\`\`${change.language}\n${change.code}\n\`\`\``,
    },
  ]
}

export function applyMultipleChangesPrompt(
  originalFileContents: string,
  changes: {
    language: string
    location: string
    type: 'edit' | 'add' | 'remove' | 'entire_file'
    code: string
  }[],
): { role: 'user'; content: string }[] {
  if (changes.length === 0) {
    throw new Error('No changes provided.')
  }
  const originalFileCodeBlock = `\`\`\`${changes[0]!.language}\n${originalFileContents}\n\`\`\``
  return [
    {
      role: 'user',
      content: `You are given a code file and ${changes.length} blocks of code to place in the file. Edit the file to include the blocks of code. ${OutputEntireFilePrompt}`,
    },
    {
      role: 'user',
      content: originalFileCodeBlock,
    },
    ...changes.map(
      (change) =>
        ({
          role: 'user',
          content: `Change location: ${change.location}\n\`\`\`${change.language}\n${change.code}\n\`\`\``,
        }) as { role: 'user'; content: string },
    ),
  ]
}
